import { useState } from 'react';
import { Header } from './components/Header';
import { TabBar } from './components/TabBar';
import { PeriodLabel } from './components/PeriodLabel';
import { KyuseiCard } from './components/KyuseiCard';
import { ShichuCard } from './components/ShichuCard';
import { SougouCard } from './components/SougouCard';
import { A2HSHelp } from './components/A2HSHelp';
import { useFortune } from './hooks/useFortune';
import { todayInJst } from './lib/apiClient';
import type { PeriodKind } from './lib/types';

export default function App() {
  const [period, setPeriod] = useState<PeriodKind>('today');
  const date = todayInJst();
  const q = useFortune(period, date);

  return (
    <div className="min-h-screen flex flex-col">
      <Header userInfo={q.data?.fortune.userInfo} />
      <TabBar value={period} onChange={setPeriod} />

      <PeriodLabel period={period} referenceDate={date} />

      <main className="flex-1">
        {q.isLoading && <LoadingState />}
        {q.isError && <ErrorState message={q.error.message} retry={() => q.refetch()} />}
        {q.data && (
          <>
            <KyuseiCard data={q.data.fortune.kyusei} />
            <ShichuCard data={q.data.fortune.shichu} />
            <SougouCard data={q.data.fortune.sougou} />
            <CacheBadge cached={q.data.cached} generatedAt={q.data.generatedAt} />
          </>
        )}
      </main>

      <A2HSHelp />
    </div>
  );
}

function LoadingState() {
  return (
    <div className="max-w-device w-full mx-auto px-4 mt-8">
      <div className="card animate-pulse">
        <div className="h-4 w-24 bg-ink-200 dark:bg-ink-700 rounded mb-3" />
        <div className="h-3 w-3/4 bg-ink-200 dark:bg-ink-700 rounded mb-2" />
        <div className="h-3 w-2/3 bg-ink-200 dark:bg-ink-700 rounded mb-2" />
        <div className="h-3 w-1/2 bg-ink-200 dark:bg-ink-700 rounded" />
      </div>
      <p className="text-center text-xs mt-6 text-ink-500 dark:text-ink-300">
        Claude が占術ソースを統合中…(初回は10〜20秒かかります)
      </p>
    </div>
  );
}

function ErrorState({ message, retry }: { message: string; retry: () => void }) {
  return (
    <div className="max-w-device w-full mx-auto px-4 mt-8">
      <div className="card border-rose-200 dark:border-rose-800/40 bg-rose-50/60 dark:bg-rose-950/30">
        <h3 className="font-semibold text-rose-700 dark:text-rose-300">取得に失敗しました</h3>
        <p className="mt-2 text-xs text-ink-700 dark:text-ink-200 whitespace-pre-wrap">{message}</p>
        <button
          onClick={retry}
          className="mt-4 inline-flex items-center rounded-full bg-rose-600 text-white px-4 py-1.5 text-sm font-medium hover:bg-rose-700"
        >
          再試行
        </button>
      </div>
    </div>
  );
}

function CacheBadge({ cached, generatedAt }: { cached: boolean; generatedAt: string }) {
  const ts = new Date(generatedAt);
  const jst = new Date(ts.getTime() + 9 * 3600 * 1000);
  const h = String(jst.getUTCHours()).padStart(2, '0');
  const m = String(jst.getUTCMinutes()).padStart(2, '0');
  return (
    <p className="text-center text-[10px] text-ink-400 dark:text-ink-500 my-4 pb-4">
      {cached ? 'キャッシュ' : '新規生成'} ／ {jst.getUTCMonth() + 1}/{jst.getUTCDate()} {h}:{m} JST
    </p>
  );
}
